import React from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { createStructuredSelector } from 'reselect';
import {
  grid,
  width,
  space,
  color,
  border,
  layout,
  shadow,
  system,
  flexbox,
  position,
  background,
  typography,
} from 'styled-system';
// features
import { makeSelectExpandedContainerOpened } from '../features/expanded-container/selectors';
// helpers/constants
import * as G from '../helpers';
//////////////////////////////////////////////////

export const Box = styled.div`
  ${space}
  ${width}
  ${color}
  ${border}
  ${layout}
  ${shadow}
  ${flexbox}
  ${position}
  ${background}
  ${typography}

  float: ${({ float }) => float};
  resize: ${({ resize }) => resize};
  cursor: ${({ cursor }) => cursor};
  opacity: ${({ opacity }) => opacity};
  text-wrap: ${({ textWrap }) => textWrap};
  transform: ${({ transform }) => transform};
  animation: ${({ animation }) => animation};
  word-break: ${({ wordBreak }) => wordBreak};
  transition: ${({ transition }) => transition};
  visibility: ${({ visibility }) => visibility};
  white-space: ${({ whiteSpace }) => whiteSpace};
  user-select: ${({ userSelect }) => userSelect};
  grid-column: ${({ gridСolumn }) => gridСolumn};
  text-overflow: ${({ textOverflow }) => textOverflow};
  pointer-events: ${({ pointerEvents }) => pointerEvents};
  vertical-align: ${({ verticalAlign }) => verticalAlign};
  text-transform: ${({ textTransform }) => textTransform};
  text-decoration: ${({ textDecoration }) => textDecoration};
  backdrop-filter: ${({ backdropFilter }) => backdropFilter};
`;

const gap = system({
  gap: {
    property: 'gap',
    transform: (value) => G.ifElse(G.notContain('px', value), `${value}px`, value),
  },
});

export const Flex = styled(Box)`
  ${gap}
`;

Flex.defaultProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'normal',
};

export const FlexDir = styled(Flex)`direction: ${({ direction }) => direction || 'ltr'} ;`;

export const HideIfUserTypeCarrierBox = (props) => {
  const { isFlex, children } = props;

  if (G.isCurrentUserTypeCarrier()) return null;

  if (isFlex) return <Flex {...props}>{children}</Flex>;

  return <Box {...props}>{children}</Box>;
};

const mapStateToProps = (state) => createStructuredSelector({
  opened: makeSelectExpandedContainerOpened(state),
});

export const HideIfExpandedContainerOpenedBox = connect(mapStateToProps)((props) => {
  const { isFlex, opened, children } = props;

  if (G.isTrue(opened)) return null;

  if (isFlex) return <Flex {...props}>{children}</Flex>;

  return <Box {...props}>{children}</Box>;
});

export const HideIfExpandedContainerOpenedFromPageBox = connect(mapStateToProps)((props) => {
  const { isFlex, opened, children, openedFromPage } = props;

  if (R.and(G.isTrue(opened), R.pathEq(openedFromPage, ['location', 'pathname'], window))) return null;

  if (isFlex) return <Flex {...props}>{children}</Flex>;

  return <Box {...props}>{children}</Box>;
});

export const Grid = styled(Box)`
  ${grid}
  ${gap}
`;

Grid.defaultProps = {
  display: 'grid',
};

export const GridItem = styled(Box)`
  grid-column-end: ${({ gridColumnEnd }) => gridColumnEnd};
  grid-column-start: ${({ gridColumnStart }) => gridColumnStart};
`;

export const RelativeBox = styled(Box)`
  position: relative;
`;

export const RelativeFlex = styled(Flex)`
  position: relative;
`;

export const AbsoluteBox = styled(Flex)`
  position: absolute;
`;

export const StickedBox = styled(Box)`
  position: sticky;
`;

StickedBox.defaultProps = {
  zIndex: 1,
};

export const StickedFlex = styled(Flex)`
  position: sticky;
`;

StickedFlex.defaultProps = {
  zIndex: 1,
};

export const StickedGrid = styled(Grid)`
  position: sticky;
`;

StickedGrid.defaultProps = {
  zIndex: 1,
};

export const FixedBox = styled(Box)`
  position: fixed;
`;

export const FixedFlex = styled(Flex)`
  position: fixed;
`;

export const BoxHovered = styled(Box)`
  & .hover-visible { visibility: hidden };
  &:hover {
    color: ${({ hoverColor }) => hoverColor};
    font-weight: ${({ hoverFontWeight }) => hoverFontWeight};
    border-left: ${({ hoverBorderLeft }) => hoverBorderLeft};
    text-decoration: ${({ hoverTextDecoration }) => hoverTextDecoration};
    background-color: ${({ hoverBackgroundColor }) => hoverBackgroundColor};
    & .hover-visible { visibility: visible };
  }
`;

export const FlexHovered = styled(Flex)`
  &:hover {
    border: ${({ border }) => border};
    background-color: ${({ bgColor }) => bgColor};
    transition: ${({ transition }) => transition};
    font-weight: ${({ fontWeightHover }) => fontWeightHover};
  }
`;

export const BoxWithChildDiv = styled(Box)`
  & > div {
    width: ${({ childWidth }) => childWidth};
  }
`;

export const SectionsDivider = styled.div`
  ${space}

  height: 1px;
  width: 100%;
  border-top: solid 1px;

  border-color: ${({ borderColor }) => borderColor || G.getTheme('colors.light.darkGrey')};
`;

export const FlexDivider = styled(Flex)`
  border: none;
  border-right: solid 1px;

  border-color: ${({ borderColor }) => borderColor || G.getTheme('colors.light.darkGrey')};
`;

export const Divider = ({ my, mb, text, width }) => (
  <Flex width={width} my={my || '5px'} mb={mb || my || '5px'}>
    <Box height='1px' flexGrow='1' bg={G.getTheme('colors.dark.grey')} />
    { text && <Box px='5px' color={G.getTheme('colors.dark.grey')}>{text}</Box> }
    <Box height='1px' flexGrow='1' bg={G.getTheme('colors.dark.grey')} />
  </Flex>
);

export const DisableEventsBox = styled(Box)`
  pointer-events: ${({ disableEvents }) => G.ifElse(disableEvents, 'none', 'unset')};
`;
